import type { ReactNode } from 'react';
import type { Step, StepEmbedMedia, StepImage, StepVideoMedia } from './types';
import { StepLines } from './StepLines';
import { StepComments } from './StepComments';
import { StepImageGallery } from './StepImageGallery';
import { cn } from '@ifixit/ui/lib/utils';

/**
 * Ordinary step view: the step media, wiki-HTML text lines, and read-only
 * comments. Chaptered guide-level videos live in VideoStepScreen.
 *
 * The paginated app renders one step at a time and names it in the header, so it
 * passes no `heading`. The full (all-steps) embed stacks every step in one scroll
 * with no header, so it passes `heading` to label each step inline and tunes the
 * outer spacing through `className`.
 */
export function StepScreen({
   step,
   heading,
   className,
   footer,
   onImageChange,
}: {
   step: Step;
   heading?: string;
   className?: string;
   // The paginated app's pager; the full embed scrolls instead and passes none.
   footer?: ReactNode;
   onImageChange?: (imageIndex: number) => void;
}) {
   const images = Array.isArray(step.media?.data) ? (step.media.data as StepImage[]) : [];
   const hasMedia =
      (step.media?.type === 'image' && images.length > 0) ||
      step.media?.type === 'video' ||
      step.media?.type === 'embed';

   return (
      <section
         className={cn(
            'mx-auto grid max-w-[1180px] items-start gap-x-6 gap-y-3 px-6 pb-6 pt-20',
            // Even columns: the old 1fr / 420px split starved the instructions to
            // a ~40-character measure next to an oversized photo.
            hasMedia ? 'grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]' : 'grid-cols-1',
            className
         )}
      >
         {heading && (
            <h2 className="col-span-full m-0 text-xl font-semibold leading-tight text-gray-950">
               {heading}
            </h2>
         )}
         {hasMedia && <StepMedia step={step} images={images} onImageChange={onImageChange} />}

         <div className="min-w-0">
            <StepLines lines={step.lines} />
         </div>

         {/* The paginated screen puts nothing between the step text and the
             comments, so they keep the rule that separates them. In the full view
             the card edge already does that job. */}
         <StepComments
            step={step}
            className={cn('col-span-full', !heading && 'mt-6 border-t border-gray-300 pt-[18px]')}
         />

         {footer}
      </section>
   );
}

function StepMedia({
   step,
   images,
   onImageChange,
}: {
   step: Step;
   images: StepImage[];
   onImageChange?: (imageIndex: number) => void;
}) {
   if (step.media.type === 'image') {
      return <StepImageGallery images={images} onImageChange={onImageChange} />;
   }

   if (step.media.type === 'embed') {
      const html = (step.media.data as StepEmbedMedia).html ?? '';
      return (
         <div
            className="relative my-4 w-full pb-[56.25%] [&_iframe]:absolute [&_iframe]:inset-0 [&_iframe]:size-full [&_iframe]:border-0"
            // The API returns sanitized oEmbed HTML, matching the old innerHTML path.
            dangerouslySetInnerHTML={{ __html: html }}
         />
      );
   }

   const video = step.media.data as StepVideoMedia;
   const poster = video.image?.image?.medium;
   return (
      <div className="py-4">
         <video
            className="block h-auto max-h-[65vh] w-full max-w-full rounded-2xl object-contain"
            muted
            autoPlay
            controls
            loop={Boolean(App.videoLooping)}
            preload="auto"
            poster={typeof poster === 'string' ? poster : undefined}
         >
            {(video.encodings ?? []).map(encoding => (
               <source key={encoding.url} src={encoding.url} type={encoding.mime} />
            ))}
         </video>
      </div>
   );
}
